<template>
  <f7-page :page-content="false">
    <f7-navbar title="Items enriquecidos" :back-link="$t('atras')"></f7-navbar>
    <f7-page-content class="full-height">
      <div v-if="reprocesando || avance" class="reproceso-banner">
        <i class="f7-icons" style="font-size:16px;">arrow_2_circlepath</i>
        <span v-if="avance" class="reproceso-texto">{{ avance.texto }}</span>
        <span v-if="avance && avance.total" class="text-color-gray" style="font-size:11px;">
          {{ avance.hechos }} / {{ avance.total }}
        </span>
        <f7-button v-if="reprocesando" small round color="red" style="margin-left:auto;"
                   :disabled="cancelando" @click="cancelarReproceso">
          {{ cancelando ? 'Cancelando…' : 'Cancelar' }}
        </f7-button>
      </div>
      <r-dynamic-list
        ref="rlist"
        :dbAdapter="ItemEnriquecido"
        :tableCode="'ITEMS_ENRIQUECIDOS'"
        :tableMode="true"
        :virtualListParams="{height:32, rowsBefore:60, rowsAfter:60}"
        class="card full-height data-table"
        classContent="card-content full-height"
        :massiveActions="true"
        :controlsWidth="100"
      >
        <template v-slot:tableLinks>
          <a class="link" href="/itemsenriquecidos/new/">{{$t('common.nueva')}}</a>
        </template>
        <!-- Reclasificar en tanda: re-sincroniza los expedientes de los items marcados contra el
             maestro actual. Va en serie y en segundo plano, narrando por el hub SSE. -->
        <template v-slot:massiveActions="{ selecteds }">
          <!-- El contador va con la misma expresión que la cabecera del componente; llamar aquí
               a un método del padre devolvía 0 aunque `selecteds` trajera la selección. -->
          <f7-button v-if="selecteds && Object.keys(selecteds).length" small fill color="blue"
                     :disabled="reprocesando" @click="reclasificarSeleccionados(selecteds)">
            {{ reprocesando ? 'Reclasificando…' : 'Reclasificar seleccionados (' + Object.keys(selecteds).length + ')' }}
          </f7-button>
        </template>
        <template v-slot:column-eventos="{item}">
          <span v-if="!item.eventos || !item.eventos.length" class="text-color-gray">—</span>
          <span v-else class="eventos-celda">
            <a v-for="(ev, i) in item.eventos" :key="'ev-'+item.id+'-'+i"
               class="evento-chip" :class="'evento-' + String(ev.tipo || 'OTRO').toLowerCase()"
               :href="ev.idExpediente ? ('/inspecciones/view/' + encodeURIComponent(ev.idExpediente) + '/') : null"
               v-tooltip="tituloEvento(ev)">
              {{ ev.codigoExpediente || ev.idExpediente || (ev.tipo || 'evento') }}
            </a>
          </span>
        </template>
        <template v-slot:optionsColumn="{item}">
          <f7-link icon-f7="eye" color="green" :href="`/itemsenriquecidos/view/${encodeURIComponent(item.id)}/`" v-tooltip="$t('common.ver')"></f7-link>
          <f7-link icon-f7="square_pencil" color="blue" :href="`/itemsenriquecidos/edit/${encodeURIComponent(item.id)}/`" v-tooltip="$t('common.editar')"></f7-link>
          <f7-link icon-f7="trash" color="red" @click="$refs.rlist.deleteItem(item.id)" v-tooltip="$t('common.eliminar')"></f7-link>
        </template>
      </r-dynamic-list>
    </f7-page-content>
  </f7-page>
</template>

<script>
import rDynamicList from "./../../components/rDynamicList.vue";
import ItemEnriquecido from "./../../js/db/ItemEnriquecido.js";
import ReprocesoMasivoEvents from "./../../js/db/ReprocesoMasivoEvents.js";
import cerrojoDeLaSesion from "./../../js/utils/cerrojoDeLaSesion.js";

export default {
  components: {
    rDynamicList
  },
  data() {
    return {
      ItemEnriquecido,
      encodeURIComponent,
      reprocesando: false,
      cancelando: false,
      /** Último evento de avance recibido por SSE (null si no hay tanda). */
      avance: null
    };
  },
  beforeDestroy() {
    this.pararSondeo();
    // El canal es un singleton de módulo y la tanda sigue aunque se navegue: solo se
    // desengancha esta pantalla, el aviso global de fin lo da el propio módulo.
    if (this._reprocesoHandler) ReprocesoMasivoEvents.unsubscribe(this._reprocesoHandler);
  },
  methods: {
    /**
     * Lanza la reclasificación en tanda de los items marcados. Avisa antes de lo que NO es
     * evidente: que se sobrescriben las características editadas a mano y que el identificador
     * no se recalcula.
     */
    reclasificarSeleccionados(selecteds) {
      const self = this;
      // Los ids son las claves del objeto de selección del listado, {id: item}. Se calcula aquí
      // y no en un método aparte porque llamando a uno el resultado llegaba vacío.
      var sel = selecteds && Object.keys(selecteds).length
        ? selecteds
        : (self.$refs.rlist && self.$refs.rlist.selected) || {};
      var ids = Object.keys(sel);
      if (!ids.length || self.reprocesando) return;
      var aviso = [
        'Se van a reclasificar ' + ids.length + ' item(s): se re-sincronizan sus expedientes contra el maestro actual.',
        'Las características que se hayan editado a mano se sobrescriben con lo que diga el expediente (el valor anterior queda en el histórico).',
        'El identificador NO se recalcula: las plantillas nuevas solo se aplican a items nuevos.'
      ].join('<br><br>');
      self.$f7.dialog.confirm(aviso, 'Reclasificar en tanda', function () {
        // #663 · El cerrojo de la sesión: si la sesión cambia en vuelo, la frontera calla la continuación que lo soltaba.
        cerrojoDeLaSesion(self, 'reprocesando');
        self.cancelando = false;
        self.avance = { texto: 'Lanzando la tanda…', hechos: 0, total: ids.length };
        if (!self._reprocesoHandler) self._reprocesoHandler = self._onReprocesoEvent.bind(self);
        ItemEnriquecido.reclasificarMasivo(ids).then(function (res) {
          ReprocesoMasivoEvents.subscribe(self._reprocesoHandler, self.$f7);
          self.arrancarSondeo();
          if (res && res.lanzado === false && res.enCurso) {
            self.reprocesando = false;
            self.avance = null;
            self.$f7.dialog.alert('Ya hay una tanda en curso. Espera a que termine.', 'Reclasificar en tanda');
          }
        }).catch(function (err) {
          self.reprocesando = false;
          self.avance = null;
          self.$f7.dialog.alert((err && (err.message || err.error)) || 'No se pudo lanzar la tanda.', 'Reclasificar en tanda');
        });
      });
    },
    _onReprocesoEvent(payload) {
      const self = this;
      if (!payload || typeof payload !== 'object') return;
      if (payload.estado === 'progreso') {
        self.reprocesando = true;
        self.avance = { texto: payload.texto || '', hechos: payload.hechos, total: payload.total };
        return;
      }
      if (payload.estado === 'fin' || payload.estado === 'cancelado') {
        self.pararSondeo();
        self.reprocesando = false;
        self.cancelando = false;
        self.avance = { texto: payload.procesados + ' de ' + payload.total + ' procesados · '
          + payload.cambiados + ' cambiaron de clasificación'
          + (payload.errores ? ' · ' + payload.errores + ' con error' : ''),
          hechos: payload.procesados, total: payload.total };
        if ((payload.incidencias || []).length) {
          self.$f7.dialog.alert((payload.incidencias || []).slice(0, 12)
            .map(function (i) { return i.id + ': ' + i.error; }).join('<br>'), 'Items con error');
        }
        // La lista se recarga para ver los tipos/subtipos nuevos.
        if (self.$refs.rlist && self.$refs.rlist.reload) self.$refs.rlist.reload();
      }
    },
    /**
     * Pregunta el avance cada dos segundos. El SSE sigue siendo la vía rápida, pero la tanda dura
     * minutos y no puede quedarse muda si el stream no está vivo o se conectó tarde.
     */
    arrancarSondeo() {
      const self = this;
      self.pararSondeo();
      self._sondeo = setInterval(function () {
        ItemEnriquecido.estadoReprocesoMasivo().then(function (est) {
          if (!est) return;
          if (est.avance) {
            self.avance = { texto: est.avance.texto || '', hechos: est.avance.hechos, total: est.avance.total };
          }
          if (!est.enCurso) {
            self.pararSondeo();
            if (est.ultimoResumen) self._onReprocesoEvent(Object.assign({ estado: 'fin' }, est.ultimoResumen));
            else { self.reprocesando = false; self.cancelando = false; }
          }
        }).catch(function () { /* un sondeo fallido no es motivo de alarma; lo reintenta */ });
      }, 2000);
    },
    pararSondeo() {
      if (this._sondeo) { clearInterval(this._sondeo); this._sondeo = null; }
    },
    cancelarReproceso() {
      const self = this;
      if (self.cancelando) return;
      cerrojoDeLaSesion(self, 'cancelando');
      ItemEnriquecido.cancelarReprocesoMasivo().catch(function () { self.cancelando = false; });
    },
    /** Tooltip del chip de evento: tipo + código de expediente + fecha. */
    tituloEvento(ev) {
      var partes = [];
      if (ev.tipo) partes.push(ev.tipo);
      if (ev.codigoExpediente) partes.push(ev.codigoExpediente);
      if (ev.fecha) {
        try {
          var d = new Date(ev.fecha);
          partes.push(d.toLocaleDateString('es-ES'));
        } catch (e) {}
      }
      return partes.join(' · ');
    }
  }
};
</script>

<style scoped>
.reproceso-banner {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px 12px;
  background: #eef5ff;
  border-bottom: 1px solid #cfe0f7;
  font-size: 12px;
  color: #1565c0;
}
.reproceso-texto {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 60%;
}
.eventos-celda {
  display: inline-flex;
  gap: 4px;
  overflow: hidden;
  white-space: nowrap;
}
.evento-chip {
  display: inline-block;
  padding: 0 6px;
  border-radius: 9px;
  font-size: 11px;
  line-height: 17px;
  background: #e3f0fb;
  color: #1a6fb5;
  border: 1px solid #c4dff5;
  text-decoration: none;
}
.evento-chip.evento-inspeccion {
  background: #e8f5e9;
  color: #2e7d32;
  border-color: #c8e6c9;
}
</style>
